import type {
  EditorConfig,
  LexicalEditor,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import { DecoratorNode } from 'lexical';
import type { JSX } from 'react';
import { RobotIcon, WrenchIcon } from '@phosphor-icons/react';
import { useMentionSources } from './MentionContext';

export type SerializedMentionNode = Spread<
  {
    mentionKey: string;
    mentionId: string;
  },
  SerializedLexicalNode
>;

/** Chip rendered inside the editor for a saved mention. */
function MentionChip({
  mentionKey,
  mentionId,
}: {
  mentionKey: string;
  mentionId: string;
}): JSX.Element {
  const sources = useMentionSources();
  const source = sources.find((s) => s.key === mentionKey);
  const displayName = source?.resolveDisplayName(mentionId) ?? mentionId;
  const isAgent = source?.resolveKind?.(mentionId) === 'agent';
  const Icon = isAgent ? RobotIcon : WrenchIcon;

  return (
    <span
      className="lexical-mention"
      data-mention-key={mentionKey}
      data-mention-id={mentionId}
      contentEditable={false}
    >
      <Icon
        size={10}
        color={isAgent ? '#4f46e5' : undefined}
        style={{ marginRight: '2px' }}
      />
      {displayName}
    </span>
  );
}

export class MentionNode extends DecoratorNode<JSX.Element> {
  __mentionKey: string;
  __mentionId: string;

  static getType(): string {
    return 'mention';
  }

  static clone(node: MentionNode): MentionNode {
    return new MentionNode(node.__mentionKey, node.__mentionId, node.__key);
  }

  constructor(mentionKey: string, mentionId: string, key?: NodeKey) {
    super(key);
    this.__mentionKey = mentionKey;
    this.__mentionId = mentionId;
  }

  createDOM(_config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    return span;
  }

  updateDOM(): boolean {
    // React handles updates via decorate()
    return false;
  }

  isInline(): boolean {
    return true;
  }

  isKeyboardSelectable(): boolean {
    return true;
  }

  exportJSON(): SerializedMentionNode {
    return {
      ...super.exportJSON(),
      mentionKey: this.__mentionKey,
      mentionId: this.__mentionId,
      type: 'mention',
      version: 1,
    };
  }

  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    return $createMentionNode(
      serializedNode.mentionKey,
      serializedNode.mentionId,
    );
  }

  decorate(_editor: LexicalEditor, _config: EditorConfig): JSX.Element {
    return (
      <MentionChip
        mentionKey={this.__mentionKey}
        mentionId={this.__mentionId}
      />
    );
  }
}

export function $createMentionNode(
  mentionKey: string,
  mentionId: string,
): MentionNode {
  return new MentionNode(mentionKey, mentionId);
}

export function $isMentionNode(node: unknown): node is MentionNode {
  return node instanceof MentionNode;
}
